import React from 'react';
import './CompetitionVisibilityPill.css';

type CompetitionVisibility = 'public' | 'private' | 'unlisted' | 'showcase' | 'unknown';

function normalizeVisibility(input?: string | null): CompetitionVisibility {
  const raw = (input || '').trim().toLowerCase();
  if (raw === 'public' || raw === 'private' || raw === 'unlisted' || raw === 'showcase') {
    return raw;
  }
  return 'unknown';
}

function labelForVisibility(visibility: CompetitionVisibility): string {
  switch (visibility) {
    case 'private':
      return 'Private';
    case 'public':
      return 'Public';
    case 'unlisted':
      return 'Unlisted';
    case 'showcase':
      return 'Showcase';
    default:
      return 'Unknown';
  }
}

function iconForVisibility(visibility: CompetitionVisibility): React.ReactElement {
  switch (visibility) {
    case 'private':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <rect x="5" y="11" width="14" height="10" rx="2" ry="2" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
      );
    case 'unlisted':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 12s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5Z" />
          <line x1="4" y1="4" x2="20" y2="20" />
        </svg>
      );
    case 'public':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3a14 14 0 0 1 0 18" />
          <path d="M12 3a14 14 0 0 0 0 18" />
        </svg>
      );
    case 'showcase':
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3.5 14.8 9l6 .6-4.5 3.9 1.4 5.7L12 16l-5.7 3.2 1.4-5.7L3.2 9.6l6-.6L12 3.5Z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v5" />
          <circle cx="12" cy="16" r="1" />
        </svg>
      );
  }
}

export interface CompetitionVisibilityPillProps {
  visibility?: string | null;
  className?: string;
  showLabel?: boolean;
}

const CompetitionVisibilityPill: React.FC<CompetitionVisibilityPillProps> = ({
  visibility,
  className = '',
  showLabel = false,
}) => {
  const normalized = normalizeVisibility(visibility);
  const label = labelForVisibility(normalized);

  return (
    <span
      className={`competition-visibility-pill competition-visibility-pill--${normalized} ${className}`.trim()}
      title={`Visibility: ${label}`}
      aria-label={`Visibility: ${label}`}
    >
      {iconForVisibility(normalized)}
      {showLabel && <span className="competition-visibility-pill__label">{label}</span>}
    </span>
  );
};

export default CompetitionVisibilityPill;
